import {useRolesStore} from '@/stores/roles'
import {useAuthStore} from '@/stores/auth'
import {useEntitiesStore} from '@/stores/entities'
import {useViewsStore} from '@/stores/views'
import Srv from '@/services/GraphQLSrv'

// Promise that resolves when init completes — used by router guard to wait for permissions.
// Пересоздаётся при каждом новом запуске init(), иначе после первого resolve все
// последующие `await initReady` возвращаются мгновенно, и guard пускает юзера с
// ещё не догрузившимися стор-данными.
let initResolve: (() => void) | null = null
export let initReady: Promise<void> = new Promise((resolve) => {
  initResolve = resolve
})

export default async function init(callback?: () => void): Promise<void> {
  // Если предыдущий init уже resolve'нул initReady — создаём свежий promise
  // ДО любого await, чтобы router guard, читающий initReady, ждал именно этот запуск.
  if (!initResolve) {
    initReady = new Promise((resolve) => {
      initResolve = resolve
    })
  }

  const authStore = useAuthStore()

  if (authStore.isAuth) {
    try {
      const rolesStore = useRolesStore()
      const entitiesStore = useEntitiesStore()
      const viewsStore = useViewsStore()

      // Один батч-запрос вместо 4 отдельных HTTP запросов
      // views, roles, entities, MeQuery — объединены в один GraphQL документ
      let batchData: any
      try {
        batchData = await Srv.batchQuery([
          {name: 'views', fields: ['uuid', 'data'], includePagination: true, params: {perPage: 5000}},
          {name: 'roles', fields: ['uuid', 'name', 'attributes'], includePagination: true, params: {page: 1, perPage: 20}},
          {name: 'entities', fields: ['uuid', 'title', 'name', 'methods'], includePagination: true, params: {perPage: 500}},
          {name: 'MeQuery', fields: ['uuid', 'login', 'roleUuids', 'publicKey', 'attributes'], includePagination: false},
        ])
      } catch (batchErr: any) {
        // GraphQL partial-success: {data: {...}, errors: [...]} — у юзера часть полей недоступна по правам.
        // Используем то, что вернулось, чтобы дать пройти flow до экрана /no-access.
        if (batchErr?.data && batchErr?.errors) {
          console.warn('init: partial batch response, some queries denied', batchErr.errors)
          batchData = batchErr.data
        } else {
          throw batchErr
        }
      }

      // Гидрация сторов из батч-данных (без дополнительных HTTP запросов)
      viewsStore.hydrateViews(batchData.views?.data ?? [])
      rolesStore.hydrateRoles(batchData.roles?.data ?? [])
      entitiesStore.hydrateEntities(batchData.entities?.data ?? [])

      // MeQuery не имеет пагинации — данные приходят напрямую
      // После гидрации загружаем permissions (зависит от roleUuids из MeQuery)
      await authStore.hydrateMeAndPermissions(batchData.MeQuery ?? {})
    } catch (err) {
      console.error('Init failed:', err)
      // При ошибке инициализации (401 уже обработан в batchQuery — logout и редирект)
      // Для остальных ошибок разлогиниваем, чтобы не оставлять черный экран
      if (authStore.isAuth) {
        await authStore.logout()
      }
    }
  }

  if (initResolve) {
    initResolve()
    initResolve = null
  }
  if (callback) callback()
}
